import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { accountsService } from '@/services/accounts.service';
import { ApiError } from '@/services/http';
import { useAccountStore } from '@/store/account.store';
import { cn } from '@/lib/cn';

/**
 * ParentPinDialog — modal that gates an off-site click behind the
 * family's 4-digit parent PIN. Used by the "Buy on Amazon" link on
 * book detail; designed to be drop-in reusable for any other
 * parent-gated affordance we add later (subscription cancel, perhaps).
 *
 * Two branches:
 *
 *   1. **PIN set** (`account.hasParentPin === true`): renders a 4-digit
 *      entry, calls `accountsService.verifyParentPin`, and on success
 *      invokes `onVerified`. On 401 the dialog reseeds for another
 *      attempt; other failures surface a friendly error.
 *
 *   2. **PIN NOT set** (`account.hasParentPin === false`): the gate is
 *      load-bearing for the parental contract, so we don't pop the link
 *      directly. Instead the dialog explains the situation and offers a
 *      shortcut to `/account` to set up the PIN. This matches the
 *      "Recommended" choice the product made — no kid can ever click
 *      through before a parent has explicitly set the PIN.
 *
 * Focus is placed on the first digit input on open. Autocomplete is
 * disabled outright (one-time challenge, not a sign-in).
 */
export interface ParentPinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Friendly label for what the gate is protecting. Surfaces in the
   * dialog body so the parent (or older kid) knows what they're about
   * to unlock. e.g. "Open the Amazon page for Frog and Toad?"
   */
  title?: string;
  /** Plain-English copy under the title; defaults to a generic prompt. */
  description?: string;
  /** Fired once the server has accepted the PIN. */
  onVerified: () => void;
}

export function ParentPinDialog({
  open,
  onOpenChange,
  title = 'Enter parent PIN',
  description = 'Ask a grown-up to enter the 4-digit PIN to continue.',
  onVerified,
}: ParentPinDialogProps) {
  const account = useAccountStore((s) => s.account);
  const hasPin = account?.hasParentPin === true;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-or-slate-900/55 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-32px)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-or-slate-100 bg-white p-6 shadow-or-pop focus:outline-none"
          aria-describedby="parent-pin-description"
        >
          <div className="flex items-start justify-between gap-3">
            <Dialog.Title className="font-display text-[20px] font-bold text-or-slate-900">
              {hasPin ? title : 'Set up a parent PIN first'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="-mr-1 -mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-or-slate-500 transition hover:bg-or-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </Dialog.Close>
          </div>

          {hasPin ? (
            <PinEntry
              description={description}
              onCancel={() => onOpenChange(false)}
              onVerified={() => {
                onVerified();
                onOpenChange(false);
              }}
            />
          ) : (
            <PinSetupPrompt onClose={() => onOpenChange(false)} />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PinSetupPrompt({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  return (
    <>
      <div
        id="parent-pin-description"
        className="mt-3 flex items-start gap-3 rounded-2xl border border-or-orange-100 bg-or-orange-50 p-4 text-[13px] text-or-orange-900"
      >
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <div>
          To keep off-site links safe for your readers, OllieReads asks for a 4-digit parent
          PIN before opening external buy links. Set one up in <span className="font-semibold">Account → Parent PIN</span> and then try again.
        </div>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            onClose();
            navigate('/account');
          }}
        >
          Open Account settings
        </Button>
      </div>
    </>
  );
}

interface PinEntryProps {
  description: string;
  onCancel: () => void;
  onVerified: () => void;
}

function PinEntry({ description, onCancel, onVerified }: PinEntryProps) {
  // The PIN is stored as four single-digit slots so we can drive the
  // focus-on-type UX without a third-party input library. Submission
  // joins them into a single 4-char string before posting.
  const [digits, setDigits] = useState<string[]>(['', '', '', '']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // Autofocus the first slot on open. Radix waits a frame to mount the
  // content so an `autoFocus` on the input alone can race; the manual
  // focus call inside an effect is deterministic.
  useEffect(() => {
    const t = window.setTimeout(() => {
      inputsRef.current[0]?.focus();
    }, 30);
    return () => window.clearTimeout(t);
  }, []);

  const updateDigit = (index: number, raw: string) => {
    // Allow paste of multiple digits — distribute starting at `index`.
    const clean = raw.replace(/\D/g, '');
    if (clean.length === 0) {
      setDigits((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      return;
    }
    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < clean.length && index + i < 4; i += 1) {
        next[index + i] = clean[i]!;
      }
      return next;
    });
    // Advance focus to the slot after the last one we just filled.
    const nextFocus = Math.min(index + clean.length, 3);
    inputsRef.current[nextFocus]?.focus();
    setError(null);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && digits[index] === '' && index > 0) {
      // Backspace on empty slot jumps to the previous one and clears it.
      inputsRef.current[index - 1]?.focus();
      setDigits((prev) => {
        const next = [...prev];
        next[index - 1] = '';
        return next;
      });
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < 3) {
      inputsRef.current[index + 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'Enter') {
      void handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const pin = digits.join('');
    if (pin.length !== 4 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await accountsService.verifyParentPin(pin);
      onVerified();
    } catch (err) {
      // Server returns 403 for "wrong PIN" (not 401) — see
      // `AccountsService.verifyParentPin`. 401 would have triggered the
      // global session-expired handler in `services/http` and bounced
      // the user back to /login on every wrong attempt.
      if (err instanceof ApiError && err.status === 403) {
        setError("That PIN didn't match. Try again.");
      } else if (err instanceof ApiError && err.status === 404) {
        setError('No parent PIN is set for this account.');
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not verify the PIN.');
      }
      // Wipe the digits + refocus the first slot so the parent can
      // retry without backspacing four times.
      setDigits(['', '', '', '']);
      inputsRef.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      <p id="parent-pin-description" className="mt-2 text-[13px] leading-relaxed text-or-slate-600">
        {description}
      </p>
      <div className="mt-5 flex items-center justify-center gap-2">
        {[0, 1, 2, 3].map((index) => (
          <input
            key={index}
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            // `password` masks the digit (shows a bullet) so the parent PIN
            // isn't readable over the shoulder; `inputMode="numeric"` keeps
            // the numeric keypad on mobile.
            type="password"
            inputMode="numeric"
            // Auto-fill / password manager interception is unhelpful for a
            // one-time challenge. `one-time-code` is the standard token
            // that keeps password managers out of this field.
            autoComplete="one-time-code"
            maxLength={4}
            value={digits[index]}
            onChange={(e) => updateDigit(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={submitting}
            aria-label={`Digit ${index + 1} of 4`}
            className={cn(
              'h-14 w-12 rounded-xl border border-or-slate-200 bg-white text-center font-display text-[22px] font-bold text-or-slate-900',
              'focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-500/30',
              error ? 'border-or-rose-300' : '',
            )}
          />
        ))}
      </div>
      {error ? (
        <p className="mt-3 text-center text-[12px] font-semibold text-or-rose-700" role="alert">
          {error}
        </p>
      ) : null}
      <div className="mt-5 flex items-center justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={digits.join('').length !== 4 || submitting}>
          {submitting ? 'Checking…' : 'Continue'}
        </Button>
      </div>
    </form>
  );
}
